'use client';

import { usePathname } from 'next/navigation';
import { type PropsWithChildren, Suspense } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AutoRegistrationModal } from '@/components/auth';
import { EmailConfirmPopup } from '@/components/EmailConfirm';
import { GlobalTooltip } from '@/components/GlobalTooltip';
import { PaymentNotification } from '@/components/PaymentNotification';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import LobbyPageSkeleton from '@/components/skeletons/LobbyPageSkeleton';
import { useBonusCode } from '@/hooks/bonusCode';
import { useComparingLanguagesWithUserCountry } from '@/hooks/comparingLanguages';
import { useCookiesModal } from '@/hooks/cookies';
import { useJackpotWinnerModal } from '@/hooks/jackpotWinner';
import { useKnowYourLimit } from '@/hooks/knowYourLimit';
import { useNetworksModal } from '@/hooks/networks';
import { useServiceWorker } from '@/hooks/serviceWorker';
import { useConfirmRegistrationOrTerms } from '@/hooks/useConfirmRegistrationOrTerms';
import { useLootboxModal } from '@/hooks/useLootboxModal';
import { usePopupHeight } from '@/hooks/usePopupHeight';
import { useReferralSystemNotifications } from '@/hooks/useReferralSystemNotifications';
import { useAssignSeparatedWageringABTest } from '@/modules/abTesting/hooks/useAssignSeparatedWageringABTest';
import { useLicenseScript } from '@/modules/common';
import { useMissionModal } from '@/modules/common/hooks/useMissionModal';
import { useTrackingUserParamsHook } from '@/modules/userTrackingParams';
import { useGetCommonStore } from '@/store';
import { ScClientLayoutContainer } from './styled';

interface ClientCommonLayoutProps {
  locale: string;
}

export const ClientCommonLayout = ({
  locale,
  children,
}: PropsWithChildren<ClientCommonLayoutProps>) => {
  const useCommonStore = useGetCommonStore();

  const { isEmailConfirmationPopup } = useCommonStore(
    useShallow((state) => ({
      isEmailConfirmationPopup: state.isEmailConfirmationPopup,
    }))
  );

  const jackpotWonModal = useJackpotWinnerModal();
  const checkLocaleModal = useComparingLanguagesWithUserCountry(locale);
  const knowYourLimit = useKnowYourLimit();
  const networksModal = useNetworksModal();
  const cookiesModal = useCookiesModal();
  const lootboxModal = useLootboxModal();

  useTrackingUserParamsHook();
  useBonusCode();
  useLicenseScript();
  useConfirmRegistrationOrTerms();
  useReferralSystemNotifications();
  useAssignSeparatedWageringABTest();
  useMissionModal();

  useServiceWorker();

  const { popupHeight } = usePopupHeight();

  const pathname = usePathname();
  const isLobby =
    pathname === '/' ||
    pathname === `/${locale}` ||
    pathname.includes('/games');

  return (
    <Suspense
      fallback={isLobby ? <LobbyPageSkeleton withTitle /> : <GeneralSkeleton />}
    >
      <ScClientLayoutContainer $popupHeight={popupHeight}>
        {isEmailConfirmationPopup && <EmailConfirmPopup />}

        {children}

        <PaymentNotification />
        {cookiesModal}
        {knowYourLimit}
        {jackpotWonModal}
        {checkLocaleModal}
        {networksModal}
        {lootboxModal}

        <AutoRegistrationModal />

        <GlobalTooltip />
      </ScClientLayoutContainer>
    </Suspense>
  );
};
